
import { useState, useEffect } from "react"
import Navbar from "../components/common/Navbar"
import { Link } from "react-router-dom"


export default function AboutPage() {
  const [animatedStats, setAnimatedStats] = useState({ jobSeekers: 0, companies: 0, jobsFilled: 0, countries: 0 })
  const [isVisible, setIsVisible] = useState(false)

  // Animate stats when they come into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true)
          animateStats()
        }
      },
      { threshold: 0.5 }
    )

    const statsElement = document.getElementById("stats-section")
    if (statsElement) {
      observer.observe(statsElement)
    }

    return () => observer.disconnect()
  }, [isVisible])

  const animateStats = () => {
    const targets = { jobSeekers: 1000000, companies: 1000, jobsFilled: 10000, countries: 6 }
    const duration = 2000
    const steps = 60
    const stepTime = duration / steps

    let step = 0
    const timer = setInterval(() => {
      step++
      const progress = step / steps
      setAnimatedStats({
        jobSeekers: Math.floor(targets.jobSeekers * progress),
        companies: Math.floor(targets.companies * progress),
        jobsFilled: Math.floor(targets.jobsFilled * progress),
        countries: Math.floor(targets.countries * progress),
      })

      if (step >= steps) {
        clearInterval(timer)
        setAnimatedStats(targets)
      }
    }, stepTime)
  }

  const values = [
    {
      title: "Quality Over Quantity",
      description:
        "We focus on meaningful connections rather than overwhelming numbers, ensuring that every match has the potential to create value.",
      icon: "🎯",
      color: "from-blue-500 to-indigo-600",
    },
    {
      title: "Transparency",
      description:
        "We believe in honest communication and clear expectations for both job seekers and employers.",
      icon: "🔍",
      color: "from-green-500 to-emerald-600",
    },
    {
      title: "Continuous Innovation",
      description:
        "We constantly improve our platform and processes to deliver better results for our users.",
      icon: "🚀",
      color: "from-purple-500 to-violet-600",
    },
    {
      title: "Diversity & Inclusion",
      description:
        "We celebrate diversity and work to create equal opportunities for people of all backgrounds.",
      icon: "🤝",
      color: "from-pink-500 to-rose-600",
    },
  ]

  const timeline = [
    {
      year: "2025",
      title: "The Beginning",
      description: "Jobs247 was founded with a vision to transform recruitment",
      icon: "🌟",
    },
    {
      year: "2025",
      title: "Platform Launch",
      description: "Launched our innovative job matching platform",
      icon: "🚀",
    },
    {
      year: "2025",
      title: "Growing Community",
      description: "Reached thousands of companies and millions of job seekers",
      icon: "📈",
    },
    {
      year: "Today",
      title: "Leading Platform",
      description: "Continuing to innovate and expand our services globally",
      icon: "🏆",
    },
  ]

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-20 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-700 to-purple-700"></div>
        
        {/* Background Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-10 w-72 h-72 bg-white opacity-10 rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-10 left-10 w-64 h-64 bg-white opacity-10 rounded-full blur-2xl animate-float-delayed"></div>
          <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-white opacity-5 rotate-45 rounded-lg"></div>
          <div className="absolute bottom-1/4 right-1/4 w-24 h-24 bg-white opacity-5 rotate-12 rounded-lg"></div>
        </div>

        <div className="relative max-w-4xl mx-auto px-4 text-center text-white">
          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-3xl mb-6 shadow-2xl">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">About Us</h1>
          <p className="text-xl md:text-2xl text-indigo-100 max-w-3xl mx-auto leading-relaxed">
            Learn more about our mission to connect talented professionals with great opportunities
          </p>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 bg-white relative">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-grid-pattern"></div>
        </div>

        <div className="relative max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Story</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-indigo-600 to-purple-600 mx-auto rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Story Content */}
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Jobs247 was founded in 2025 with a simple but powerful vision: to transform how people find jobs and
                  how companies find talent. We recognized that the traditional recruitment process was often
                  inefficient, impersonal, and ineffective for both job seekers and employers.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  What started as a small team of passionate professionals has grown into a leading career platform
                  serving thousands of companies and millions of job seekers worldwide. Our platform has evolved to
                  incorporate cutting-edge technology, personalized matching algorithms, and a commitment to quality
                  that sets us apart in the industry.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Today, we continue to innovate and expand our services, always staying true to our core mission:
                  connecting the right people with the right opportunities at the right time.
                </p>
              </div>
            </div>

            {/* Timeline */}
            <div className="space-y-6">
              {timeline.map((item, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-4 group hover:transform hover:scale-105 transition-all duration-300"
                >
                  <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-2xl flex items-center justify-center text-2xl shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                    {item.icon}
                  </div>
                  <div className="flex-1 bg-white rounded-xl shadow-md p-6 group-hover:shadow-lg transition-shadow duration-300">
                    <div className="flex items-center mb-2">
                      <span className="text-sm font-bold text-indigo-600 bg-indigo-100 px-3 py-1 rounded-full">
                        {item.year}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-indigo-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Mission & Values</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-indigo-600 to-purple-600 mx-auto rounded-full"></div>
          </div>

          {/* Mission Statement */}
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden mb-16 transform hover:scale-105 transition-transform duration-300">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-8 text-white">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center mr-4">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <h3 className="text-3xl font-bold">Our Mission</h3>
              </div>
            </div>
            <div className="p-8">
              <p className="text-xl text-gray-700 leading-relaxed">
                To empower people to find meaningful careers and help organizations find the talent they need to
                thrive, creating value for both individuals and businesses through innovative technology and
                personalized service.
              </p>
            </div>
          </div>

          {/* Values Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden group"
              >
                <div className={`h-2 bg-gradient-to-r ${value.color}`}></div>
                <div className="p-8">
                  <div className="flex items-center mb-4">
                    <div className="text-4xl mr-4 group-hover:scale-110 transition-transform duration-300">
                      {value.icon}
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">{value.title}</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{value.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section id="stats-section" className="py-20 bg-gradient-to-r from-indigo-700 to-purple-700 text-white relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-32 h-32 bg-white opacity-10 rounded-full blur-2xl animate-float"></div>
          <div className="absolute bottom-10 right-10 w-24 h-24 bg-white opacity-10 rounded-full blur-xl animate-float-delayed"></div>
          <div className="absolute top-1/2 right-1/4 w-16 h-16 bg-white opacity-5 rotate-45 rounded-lg"></div>
        </div>

        <div className="relative max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Our Impact</h2>
          <p className="text-xl text-indigo-100 mb-12 max-w-2xl mx-auto">
            Numbers that showcase our commitment to connecting talent with opportunity
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/20 transition-all duration-300 transform hover:scale-105">
              <div className="text-4xl md:text-6xl font-bold mb-2">
                {animatedStats.jobSeekers >= 1000000 ? "1M+" : `${Math.floor(animatedStats.jobSeekers / 1000)}K+`}
              </div>
              <div className="text-lg md:text-xl text-indigo-200">Job Seekers</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/20 transition-all duration-300 transform hover:scale-105">
              <div className="text-4xl md:text-6xl font-bold mb-2">{animatedStats.companies}+</div>
              <div className="text-lg md:text-xl text-indigo-200">Companies</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/20 transition-all duration-300 transform hover:scale-105">
              <div className="text-4xl md:text-6xl font-bold mb-2">
                {animatedStats.jobsFilled >= 10000 ? "10K+" : `${Math.floor(animatedStats.jobsFilled / 1000)}K+`}
              </div>
              <div className="text-lg md:text-xl text-indigo-200">Jobs Filled</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/20 transition-all duration-300 transform hover:scale-105">
              <div className="text-4xl md:text-6xl font-bold mb-2">{animatedStats.countries}</div>
              <div className="text-lg md:text-xl text-indigo-200">Countries</div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      {/* <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Meet Our Team</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The passionate professionals behind Jobs247's success
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-indigo-600 to-purple-600 mx-auto rounded-full mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { name: "Sarah Johnson", role: "CEO & Founder", avatar: "SJ", color: "from-blue-500 to-indigo-600" },
              { name: "Michael Chen", role: "CTO", avatar: "MC", color: "from-green-500 to-emerald-600" },
              { name: "Emily Rodriguez", role: "Head of Product", avatar: "ER", color: "from-purple-500 to-violet-600" },
            ].map((member, index) => (
              <div
                key={index}
                className="text-center group hover:transform hover:scale-105 transition-all duration-300"
              >
                <div className="relative mb-6">
                  <div className={`w-32 h-32 bg-gradient-to-br ${member.color} rounded-full flex items-center justify-center text-white text-3xl font-bold mx-auto shadow-2xl group-hover:shadow-3xl transition-shadow duration-300`}>
                    {member.avatar}
                  </div>
                  <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-green-500 rounded-full border-4 border-white flex items-center justify-center">
                    <div className="w-3 h-3 bg-white rounded-full"></div>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{member.name}</h3>
                <p className="text-gray-600">{member.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-indigo-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="bg-white rounded-3xl shadow-2xl p-12 transform hover:scale-105 transition-transform duration-300">
            <div className="mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl mb-6 shadow-lg">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Join Our Community</h2>
            <p className="text-xl text-gray-700 mb-8 leading-relaxed">
              Whether you're looking for your next career move or searching for top talent, we're here to help you
              succeed.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/jobs">
              <button className="px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                Start Job Search
              </button>
              </Link>
              <Link to="/contact">
              <button className="px-8 py-4 bg-white text-indigo-600 border-2 border-indigo-600 font-bold rounded-xl hover:bg-indigo-50 transition-all duration-300 shadow-lg hover:shadow-xl">
                Contact Us
              </button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
